import { Link, useParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import AnimeCard from "@/components/feature/AnimeCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { animeList } from "@/mocks/anime";
import { getAnimeById, getNewsForAnime, getRelatedAnime, formatNumber } from "@/utils/content";

export default function DetailPage() {
  const { id } = useParams();
  const anime = getAnimeById(id);

  useDocumentMeta(
    anime ? `动漫天堂 - ${anime.title}剧情介绍与资讯` : "动漫天堂 - 作品未找到",
    anime
      ? `${anime.title}（${anime.year}年 · ${anime.region}）剧情介绍：${anime.summary} 查看作品类型、更新状态与相关动漫资讯。`
      : "该动漫作品不存在或已下线，请返回动漫分类页浏览其他作品。",
    anime
      ? `${anime.title},${anime.title}剧情,${anime.tags.join(",")},动漫天堂,动漫资讯`
      : "动漫天堂,动漫分类",
    `/detail/${id || ""}`
  );

  if (!anime) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteNavbar />
        <main className="flex flex-1 items-center justify-center px-4 pt-20">
          <div className="mx-auto max-w-md rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
              <i className="ri-file-search-line" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">没有找到这部动漫</h1>
            <p className="mt-2 text-sm leading-6 text-foreground-600">
              链接可能已失效，或者作品已经不在本站收录范围内。
            </p>
            <Link
              to="/category"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回动漫分类
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const relatedNews = getNewsForAnime(anime, 3);
  const relatedAnime = getRelatedAnime(anime, 3);
  const fallbackList = animeList.filter((item) => item.id !== anime.id).slice(0, 3);
  const recommendations = relatedAnime.length ? relatedAnime : fallbackList;

  const metaItems = [
    { label: "类型", value: anime.tags.join(" / "), icon: "ri-price-tag-3-line" },
    { label: "地区", value: anime.region, icon: "ri-global-line" },
    { label: "年份", value: `${anime.year} 年`, icon: "ri-calendar-line" },
    { label: "状态", value: `${anime.status} · ${anime.episodes} 集`, icon: "ri-play-list-2-line" },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full border-b border-background-200/80 bg-background-100/60">
          <div className="mx-auto w-full max-w-page px-4 pb-6 pt-6 md:px-6 md:pb-8 md:pt-8">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <Link to="/category" className="transition-colors hover:text-primary-600">动漫分类</Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">{anime.title}</span>
            </nav>
          </div>
        </section>

        <div className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[340px_1fr] lg:gap-12">
            <div>
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <div className="relative aspect-[3/4] w-full">
                  <img
                    src={anime.cover}
                    alt={`${anime.title} 动漫封面大图`}
                    title={`${anime.title} 剧情介绍`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="border-t border-background-200 bg-background-50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-400">站内评分</p>
                      <p className="mt-1 font-heading text-3xl font-extrabold text-primary-600">
                        {anime.rating.toFixed(1)}
                        <span className="ml-1 text-xs font-medium text-foreground-400">/ 10</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-400">人气</p>
                      <p className="mt-1 flex items-center justify-end gap-1 font-heading text-lg font-bold text-foreground-900">
                        <i className="ri-fire-fill text-primary-500" />
                        {formatNumber(anime.popularity)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <i
                        key={index}
                        className={
                          index < Math.round(anime.rating / 2)
                            ? "ri-star-fill text-sm text-primary-500"
                            : "ri-star-line text-sm text-background-400"
                        }
                      />
                    ))}
                    <span className="ml-2 text-[11px] text-foreground-500">演示评分，仅供参考</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">作品信息</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  {[
                    { label: "制作公司", value: anime.studio },
                    { label: "导演", value: anime.director },
                    { label: "更新时间", value: anime.updateDate },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start justify-between gap-4">
                      <dt className="shrink-0 text-foreground-500">{item.label}</dt>
                      <dd className="text-right font-medium text-foreground-800">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-semibold text-background-50">
                  编辑推荐
                </span>
                {anime.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category?type=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-4xl">
                {anime.title}
              </h1>
              <p className="mt-3 text-base leading-7 text-foreground-600">{anime.summary}</p>

              <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                {metaItems.map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-100/60 px-3 py-3">
                    <p className="flex items-center gap-1 text-[11px] text-foreground-400">
                      <i className={item.icon} />
                      {item.label}
                    </p>
                    <p className="mt-1.5 text-sm font-semibold text-foreground-900">{item.value}</p>
                  </div>
                ))}
              </div>

              <section className="mt-8">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
                </div>
                <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <p className="text-sm leading-8 text-foreground-700 md:text-[15px] md:leading-8">
                    {anime.description}
                  </p>
                </div>
              </section>

              <section className="mt-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-1 rounded-full bg-accent-500" />
                    <h2 className="font-heading text-lg font-bold text-foreground-950">相关资讯</h2>
                  </div>
                  <Link to="/news" className="text-xs font-semibold text-primary-600 hover:text-primary-700">
                    全部资讯
                  </Link>
                </div>
                <div className="mt-4 divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
                  {relatedNews.map((news) => (
                    <Link
                      key={news.id}
                      to={`/news/detail/${news.id}`}
                      className="group flex flex-col gap-1 px-4 py-4 transition-colors hover:bg-background-100/70 md:flex-row md:items-center md:justify-between md:px-5"
                    >
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 text-[11px] text-foreground-500">
                          <span className="rounded-full bg-accent-50 px-2 py-0.5 font-medium text-accent-700">
                            {news.category}
                          </span>
                          {news.date}
                        </p>
                        <h3 className="mt-1.5 line-clamp-1 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {news.title}
                        </h3>
                      </div>
                      <i className="ri-arrow-right-line hidden shrink-0 text-base text-foreground-400 transition-colors group-hover:text-primary-600 md:block" />
                    </Link>
                  ))}
                </div>
              </section>

              <section className="mt-8">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  <h2 className="font-heading text-lg font-bold text-foreground-950">相关推荐</h2>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5">
                  {recommendations.map((item) => (
                    <AnimeCard key={item.id} anime={item} />
                  ))}
                </div>
              </section>

              <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-arrow-left-line" />
                  返回分类列表
                </Link>
                <Link
                  to="/search"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-search-line" />
                  搜索其他作品
                </Link>
                <p className="text-xs text-foreground-400">本站不提供播放与下载，内容均为演示资料。</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}